import {cn} from '../../lib/utils';

import type {ComponentProps, MouseEventHandler, ReactNode} from 'react';

interface CardSectionProps {
  className?: string;
  children?: ReactNode;
}

type OmittedDivProps = 'className' | 'children' | 'onClick' | 'role' | 'tabIndex' | 'onKeyDown';

interface CardContainerProps
  extends CardSectionProps, Omit<ComponentProps<'div'>, OmittedDivProps> {
  onCardClick?: () => void;
}

interface CardFooterProps extends CardSectionProps {
  onClick?: MouseEventHandler<HTMLDivElement>;
}

export function Card({className, children, onCardClick, ...rest}: CardContainerProps) {
  const isClickable = onCardClick !== undefined;

  return (
    <div
      data-slot="card"
      className={cn(
        'flex w-full flex-col rounded-lg border border-border bg-background-surface shadow-xs transition-shadow hover:shadow-sm',
        isClickable && 'cursor-pointer',
        className
      )}
      {...rest}
      onClick={onCardClick}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={
        isClickable
          ? e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onCardClick();
              }
            }
          : undefined
      }
    >
      {children}
    </div>
  );
}

export function CardHeader({className, children}: CardSectionProps) {
  return (
    <div
      data-slot="card-header"
      className={cn('flex items-start justify-between px-5 pt-6 pb-5', className)}
    >
      {children}
    </div>
  );
}

interface CardHeaderContentProps extends CardSectionProps {
  onClick?: () => void;
}

export function CardHeaderContent({className, onClick, children}: CardHeaderContentProps) {
  return (
    <div
      data-slot="card-header-content"
      className={cn('flex min-w-0 items-center gap-3', onClick && 'cursor-pointer', className)}
      onClick={onClick}
    >
      {children}
    </div>
  );
}

export function CardBody({className, children}: CardSectionProps) {
  return (
    <div data-slot="card-body" className={cn('border-t border-border px-5 pt-4 pb-10', className)}>
      {children}
    </div>
  );
}

export function CardCell({className, children}: CardSectionProps) {
  return (
    <div data-slot="card-cell" className={cn('min-w-0', className)}>
      {children}
    </div>
  );
}

export function CardFooter({className, children, onClick}: CardFooterProps) {
  return (
    <div data-slot="card-footer" className={cn('px-3.5 pb-6', className)} onClick={onClick}>
      {children}
    </div>
  );
}
